import React, { useState, useEffect } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  CardBody,
  Alert,
  FormFeedback,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input
} from 'reactstrap';
import axios from 'axios';
import Loader from "../../../components/Loader";
import logo from "../../../assets/images/gov-logo.png";
import backend_url from "../../../routes/API_URL";

const API_URL = backend_url['api_url'];

const ResetPassword = () => {
  const { id, email, token } = useParams();
  const history = useHistory();
  
  const [formData, setFormData] = useState({
    user_password: '',
    user_password_confirm: '',
    email_id: email || '',
    id: id || '',
    bearer_auth: token || ''
  });
  
  const [validation, setValidation] = useState({
    passwordValid: false,
    passwordMatch: false,
    passwordTouched: false,
    confirmTouched: false
  });
  
  const [uiState, setUiState] = useState({
    is_loading: false,
    is_success: false,
    error: false,
    errorMessage: '',
    showPassword: false,
    showConfirmPassword: false
  });
  
  // Password strength checker
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Validate password in real-time
  useEffect(() => {
    if (formData.user_password) {
      const checks = {
        hasMinLength: formData.user_password.length >= 8,
        hasUppercase: /[A-Z]/.test(formData.user_password),
        hasLowercase: /[a-z]/.test(formData.user_password),
        hasNumber: /[0-9]/.test(formData.user_password),
        hasSpecialChar: /[#.,;~%?!@$%^&*-]/.test(formData.user_password)
      };
      
      const score = Object.values(checks).filter(Boolean).length;
      
      setPasswordStrength({
        ...checks,
        score
      });
      
      setValidation(prev => ({
        ...prev,
        passwordValid: score >= 4 && checks.hasMinLength
      }));
    } else {
      setPasswordStrength({
        score: 0,
        hasMinLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false
      });
    }
  }, [formData.user_password]);

  // Validate password match in real-time
  useEffect(() => {
    if (formData.user_password_confirm) {
      const match = formData.user_password === formData.user_password_confirm;
      setValidation(prev => ({
        ...prev,
        passwordMatch: match
      }));
    }
  }, [formData.user_password, formData.user_password_confirm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark field as touched
    if (name === 'user_password') {
      setValidation(prev => ({ ...prev, passwordTouched: true }));
    } else if (name === 'user_password_confirm') {
      setValidation(prev => ({ ...prev, confirmTouched: true }));
    }
    
    // Clear errors when user starts typing
    if (uiState.error) {
      setUiState(prev => ({ ...prev, error: false, errorMessage: '' }));
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setUiState(prev => ({ ...prev, showPassword: !prev.showPassword }));
    } else {
      setUiState(prev => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }));
    }
  };

  const validateForm = () => {
    if (!formData.user_password) {
      setUiState(prev => ({
        ...prev,
        error: true,
        errorMessage: 'Please enter a new password'
      }));
      return false;
    }
    
    if (!validation.passwordValid) {
      setUiState(prev => ({
        ...prev,
        error: true,
        errorMessage: 'Password does not meet all requirements'
      }));
      return false;
    }
    
    if (!formData.user_password_confirm) {
      setUiState(prev => ({
        ...prev,
        error: true,
        errorMessage: 'Please confirm your password'
      }));
      return false;
    }
    
    if (!validation.passwordMatch) {
      setUiState(prev => ({
        ...prev,
        error: true,
        errorMessage: 'Passwords do not match'
      }));
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setUiState(prev => ({ ...prev, is_loading: true, error: false }));
    
    try {
      const payload = {
        email_id: formData.email_id,
        user_password: formData.user_password,
        user_password_confirmation: formData.user_password_confirm,
        id: formData.id,
      };
      
      const response = await axios.patch(`${API_URL}/guest/reset_password/${formData.id}`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        timeout: 30000
      });
      
      if (response.data.success) {
        setUiState(prev => ({
          ...prev,
          is_loading: false,
          is_success: true,
          error: false
        }));
        
        // Auto-redirect after 3 seconds
        setTimeout(() => {
          history.push('/login');
        }, 3000);
      } else {
        throw new Error(response.data.message || 'Reset failed');
      }
    } catch (error) {
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'Invalid reset link. Please request a new password reset.';
        } else if (error.response.status === 400) {
          errorMessage = error.response.data.message || 'Invalid request data.';
        } else if (error.response.status === 422) {
          errorMessage = 'Password validation failed. Please check requirements.';
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message;
      }
      
      setUiState(prev => ({
        ...prev,
        is_loading: false,
        error: true,
        errorMessage
      }));
      
      // Scroll to error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Get password strength color
  const getStrengthColor = () => {
    if (passwordStrength.score === 0) return 'secondary';
    if (passwordStrength.score <= 2) return 'danger';
    if (passwordStrength.score <= 3) return 'warning';
    return 'success';
  };

  // Get password strength label
  const getStrengthLabel = () => {
    if (passwordStrength.score === 0) return 'No password';
    if (passwordStrength.score <= 2) return 'Weak';
    if (passwordStrength.score <= 3) return 'Fair';
    if (passwordStrength.score === 4) return 'Good';
    return 'Strong';
  };

  return (
    <div className="account-pages my-5 pt-5">
      <div className="container">
        <Row className="justify-content-center">
          <Col md={8} lg={6} xl={5}>
            <Card className="shadow-lg border-0">
              <div className="bg-danger">
                <div className="text-primary text-center p-4">
                  <h5 className="text-white font-size-20 mb-2">
                    RESET PASSWORD
                  </h5>
                  <Link to="/" className="logo logo-admin">
                    <img src={logo} height="45" alt="logo" />
                  </Link>
                </div>
              </div>

              <CardBody className="p-4">
                <div className="p-3">
                  {uiState.is_success ? (
                    <Alert color="success" className="text-center">
                      <i className="fas fa-check-circle fa-lg me-2"></i>
                      <strong>Success!</strong> Password has been reset. Redirecting to login...
                      <div className="mt-2">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </Alert>
                  ) : (
                    <>
                      {uiState.error && (
                        <Alert color="danger" className="d-flex align-items-center">
                          <i className="fas fa-exclamation-circle me-2"></i>
                          <div>{uiState.errorMessage}</div>
                        </Alert>
                      )}

                      <p className="text-muted mb-4">
                        Please create a new password for your account.
                      </p>

                      <Form onSubmit={handleSubmit}>
                        {uiState.is_loading && <Loader />}

                        {/* Email (readonly) */}
                        <FormGroup className="mb-3">
                          <Label for="email_id" className="form-label">
                            <i className="fas fa-envelope me-1"></i> Email Address
                          </Label>
                          <Input
                            type="email"
                            id="email_id"
                            name="email_id"
                            value={formData.email_id}
                            readOnly
                            className="bg-light"
                          />
                          <small className="text-muted">This is the email associated with your account</small>
                        </FormGroup>

                        {/* New Password */}
                        <FormGroup className="mb-3">
                          <Label for="user_password" className="form-label">
                            <i className="fas fa-lock me-1"></i> New Password
                          </Label>
                          <div className="input-group">
                            <Input
                              type={uiState.showPassword ? "text" : "password"}
                              id="user_password"
                              name="user_password"
                              value={formData.user_password}
                              onChange={handleInputChange}
                              className={`form-control ${validation.passwordTouched && !validation.passwordValid ? 'is-invalid' : ''} ${validation.passwordValid ? 'is-valid' : ''}`}
                              placeholder="Enter new password"
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => togglePasswordVisibility('password')}
                            >
                              <i className={`fas ${uiState.showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                          </div>
                          
                          {/* Password strength meter */}
                          {formData.user_password && (
                            <div className="mt-2">
                              <div className="d-flex justify-content-between mb-1">
                                <small>Password Strength:</small>
                                <small className={`text-${getStrengthColor()}`}>
                                  <strong>{getStrengthLabel()}</strong>
                                </small>
                              </div>
                              <div className="progress" style={{ height: '5px' }}>
                                <div
                                  className={`progress-bar bg-${getStrengthColor()}`}
                                  role="progressbar"
                                  style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          {/* Password requirements checklist */}
                          <div className="mt-2 small">
                            <div className="row">
                              <div className="col-6">
                                <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasMinLength ? 'text-success' : 'text-muted'}`}>
                                  <i className={`fas ${passwordStrength.hasMinLength ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                                  <span>8+ characters</span>
                                </div>
                                <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasUppercase ? 'text-success' : 'text-muted'}`}>
                                  <i className={`fas ${passwordStrength.hasUppercase ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                                  <span>Uppercase letter</span>
                                </div>
                                <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasLowercase ? 'text-success' : 'text-muted'}`}>
                                  <i className={`fas ${passwordStrength.hasLowercase ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                                  <span>Lowercase letter</span>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasNumber ? 'text-success' : 'text-muted'}`}>
                                  <i className={`fas ${passwordStrength.hasNumber ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                                  <span>Number</span>
                                </div>
                                <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasSpecialChar ? 'text-success' : 'text-muted'}`}>
                                  <i className={`fas ${passwordStrength.hasSpecialChar ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                                  <span>Special character</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </FormGroup>

                        {/* Confirm Password */}
                        <FormGroup className="mb-4">
                          <Label for="user_password_confirm" className="form-label">
                            <i className="fas fa-lock me-1"></i> Confirm Password
                          </Label>
                          <div className="input-group">
                            <Input
                              type={uiState.showConfirmPassword ? "text" : "password"}
                              id="user_password_confirm"
                              name="user_password_confirm"
                              value={formData.user_password_confirm}
                              onChange={handleInputChange}
                              className={`form-control ${validation.confirmTouched && !validation.passwordMatch ? 'is-invalid' : ''} ${validation.passwordMatch ? 'is-valid' : ''}`}
                              placeholder="Confirm new password"
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => togglePasswordVisibility('confirm')}
                            >
                              <i className={`fas ${uiState.showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                          </div>
                          {validation.confirmTouched && !validation.passwordMatch && (
                            <div className="invalid-feedback d-block">
                              <i className="fas fa-times-circle me-1"></i> Passwords do not match
                            </div>
                          )}
                          {validation.passwordMatch && (
                            <div className="valid-feedback d-block">
                              <i className="fas fa-check-circle me-1"></i> Passwords match!
                            </div>
                          )}
                        </FormGroup>

                        {/* Submit Button */}
                        <div className="d-grid gap-2">
                          <Button
                            type="submit"
                            color="primary"
                            size="lg"
                            disabled={uiState.is_loading || !validation.passwordValid || !validation.passwordMatch}
                          >
                            {uiState.is_loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Resetting Password...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-key me-2"></i>
                                Reset Password
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </>
                  )}

                  {/* Back to Login Link */}
                  <div className="mt-4 text-center">
                    <p className="mb-0">
                      Remember your password?{' '}
                      <Link to="/login" className="text-primary fw-semibold">
                        Back to Login
                      </Link>
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Footer note */}
            <div className="mt-5 text-center">
              <p className="text-muted">
                © {new Date().getFullYear()} School Inspection Information System.
                All rights reserved.
              </p>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ResetPassword;